import { isEmpty, isNil, pathOr, assocPath, uniq } from 'ramda';
import moment from 'moment';

import {
  validateRequired,
  validatePositiveMoney,
  validateDate,
} from '../../../utils/validations';
import {
  validatePairRequired,
  getLatestActiveScheduleRow,
  getHudGrossRentScheduleRows,
  getHudUaSnapshotFee,
  getHudGrossRentSnapshotFee,
  getRowScheduleFee,
} from './utils';

const sameMoney = (a: number | string, b: number) =>
  Math.round(parseFloat(String(a)) * 100) === Math.round(Number(b) * 100);

const validate = (values: Object, props: Object) => {
  const { floorPlans, touch } = props;

  let feeFields = [];
  let dateFields = [];
  Object.keys(values).forEach((key) => {
    feeFields.splice(
      feeFields.length,
      0,
      [key, 'newHudGrossRent'],
      [key, 'newHudUa'],
    );
    dateFields.splice(
      dateFields.length,
      0,
      [key, 'newHudGrossRentStartDate'],
      [key, 'newHudUaStartDate'],
    );
  });

  let errors = {};
  let fieldsWithErrors = [];

  dateFields.forEach((fieldPath) => {
    const floorPlanId = fieldPath[0];
    const fieldName = fieldPath[1];
    const floorPlan = !isNil(floorPlanId)
      ? floorPlans.find((floorPlan) => floorPlan.id === floorPlanId)
      : undefined;
    const startDate = values[floorPlanId][fieldName];

    if (!isNil(startDate) && !isEmpty(startDate)) {
      if (!validateDate(startDate)) {
        errors = assocPath(
          [floorPlanId, fieldName],
          'Please enter a valid date',
          errors,
        );
        fieldsWithErrors.push(`${floorPlanId}.${fieldName}`);
      } else if (!isNil(floorPlan)) {
        let latestStartDate;

        if (fieldName.includes('Rent')) {
          const hudGrossRents = getHudGrossRentScheduleRows(floorPlan);

          latestStartDate = pathOr(
            undefined,
            ['startDate'],
            getLatestActiveScheduleRow(
              hudGrossRents,
              getHudGrossRentSnapshotFee(floorPlan),
            ),
          );
        } else if (fieldName.includes('HudUa')) {
          const allowances =
            pathOr([], ['fpua'], floorPlan).length > 0
              ? pathOr([], ['fpua'], floorPlan)
              : pathOr([], ['allowances'], floorPlan);

          latestStartDate = pathOr(
            undefined,
            ['startDate'],
            getLatestActiveScheduleRow(
              allowances,
              getHudUaSnapshotFee(floorPlan),
            ),
          );
        }

        if (
          latestStartDate &&
          startDate
            .startOf('date')
            .isSameOrBefore(moment.utc(latestStartDate).startOf('date'))
        ) {
          errors = assocPath(
            [floorPlanId, fieldName],
            'Current start date must be after the start date of the existing current value.',
            errors,
          );
          fieldsWithErrors.push(`${floorPlanId}.${fieldName}`);
        }
      }
    }
    const { isValidPair, requiredValue } = validatePairRequired(
      values,
      floorPlanId,
      fieldName.includes('Rent'),
    );
    if (!isValidPair && !validateRequired(requiredValue, values)) {
      errors = assocPath(
        [floorPlanId, requiredValue],
        'This field is required',
        errors,
      );
      fieldsWithErrors.push(`${floorPlanId}.${fieldName}`);
    }
  });

  feeFields.forEach((fieldPath) => {
    const floorPlanId = fieldPath[0];
    const fieldName = fieldPath[1];
    const feeAmount = values[floorPlanId][fieldName];
    const floorPlan = !isNil(floorPlanId)
      ? floorPlans.find((floorPlan) => floorPlan.id === floorPlanId)
      : undefined;

    if (!isNil(feeAmount) && !isEmpty(feeAmount)) {
      if (
        !validatePositiveMoney(feeAmount) ||
        parseFloat(feeAmount) <= 0 ||
        parseFloat(feeAmount) > 9999.99
      ) {
        errors = assocPath(
          [floorPlanId, fieldName],
          'Please enter a valid amount',
          errors,
        );
        fieldsWithErrors.push(`${floorPlanId}.${fieldName}`);
      }
    }

    if (
      !isNil(floorPlan) &&
      (isNil(errors[floorPlanId]) || isNil(errors[floorPlanId][fieldName]))
    ) {
      let latestFeeAmount = 0;

      if (fieldName.includes('Rent')) {
        const hudGrossRents = getHudGrossRentScheduleRows(floorPlan);

        latestFeeAmount = pathOr(
          latestFeeAmount,
          ['feeAmount'],
          getLatestActiveScheduleRow(
            hudGrossRents,
            getHudGrossRentSnapshotFee(floorPlan),
          ),
        );
      } else if (fieldName.includes('HudUa')) {
        const allowances =
          pathOr([], ['fpua'], floorPlan).length > 0
            ? pathOr([], ['fpua'], floorPlan)
            : pathOr([], ['allowances'], floorPlan);

        const latestHud =
          getLatestActiveScheduleRow(
            allowances,
            getHudUaSnapshotFee(floorPlan),
          ) || {};

        const hudFee = getRowScheduleFee(latestHud);
        latestFeeAmount = Number.isFinite(hudFee) ? hudFee : 0;
      }

      if (
        !isNil(feeAmount) &&
        !isEmpty(feeAmount) &&
        sameMoney(feeAmount, latestFeeAmount)
      ) {
        const duplicateMsg =
          fieldName === 'newHudUa'
            ? [
                'This amount matches the current HUD Utility Allowance on the schedule.',
                'Enter a different amount, or clear both New UA fields if you are only updating gross rent.',
              ].join(' ')
            : 'Fee amount is the same as the latest existing fee.';
        errors = assocPath([floorPlanId, fieldName], duplicateMsg, errors);
        fieldsWithErrors.push(`${floorPlanId}.${fieldName}`);
      }
    }

    const { isValidPair, requiredValue } = validatePairRequired(
      values,
      floorPlanId,
      fieldName.includes('Rent'),
    );
    if (!isValidPair && !validateRequired(fieldName, values)) {
      errors = assocPath(
        [floorPlanId, requiredValue],
        'This field is required',
        errors,
      );
      fieldsWithErrors.push(`${floorPlanId}.${fieldName}`);
    }
  });

  /* This is to touch fields with errors due to being required */
  fieldsWithErrors = uniq(fieldsWithErrors);
  if (fieldsWithErrors.length > 0) {
    touch.apply(null, fieldsWithErrors);
  }

  return errors;
};

export default validate;
